import React, { useState, useEffect, useRef } from 'react';
import SwiperCore, { Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import Button, { OutlineButton } from '../button/Button';
import Modal, { ModalContent } from '../modal/Modal';
import { useNavigate } from 'react-router-dom';
import './hero-slide.scss';
import { useDispatch } from 'react-redux';
import { moviesNew } from '../../Redux/actions/MovieThunk';
import { Link } from 'react-router-dom';
import { encryptId } from '../SecurityComponent/cryptoUtils';
import { SoundOutlined, AudioMutedOutlined, PauseOutlined, CaretRightOutlined } from '@ant-design/icons';
import { FaHeart, FaShareAlt } from 'react-icons/fa';

const HeroSlide = () => {
    SwiperCore.use([Autoplay]);
    const [movieItems, setMovieItems] = useState([]);
    const [slideDelay, setSlideDelay] = useState(25000);
    const dispatch = useDispatch();

    useEffect(() => {
        const getMovies = async () => {
            try {
                const [featRes, delayRes] = await Promise.all([
                    fetch('/movies-featured-api.php'),
                    fetch('/movies-settings-api.php?key=hero_slide_delay'),
                ]);
                const [featData, delayData] = await Promise.all([featRes.json(), delayRes.json()]);

                if (delayData.success && delayData.value) {
                    setSlideDelay((parseInt(delayData.value) || 25) * 1000);
                }

                if (featData.success && featData.data?.length > 0) {
                    setMovieItems(featData.data);
                    return;
                }
                const response = await dispatch(moviesNew(3, 5));
                if (response && response.content && Array.isArray(response.content)) {
                    setMovieItems(response.content);
                }
            } catch (error) {
                console.error('Error fetching movies:', error);
            }
        }
        getMovies();
    }, [dispatch]);

    return (
        <div className="hero-slide">
            <Swiper
                modules={[Autoplay]}
                grabCursor={true}
                spaceBetween={0}
                slidesPerView={1}
                autoplay={{ delay: slideDelay, disableOnInteraction: false }}
                loop={true}
            >
                {
                    movieItems && movieItems.length > 0 ? (
                        movieItems.map((item, i) => (
                            <SwiperSlide key={i}>
                                {({ isActive }) => (
                                    <HeroSlideItem item={item} className={`${isActive ? 'active' : ''}`} isActive={isActive} />
                                )}
                            </SwiperSlide>
                        ))
                    ) : (
                        <SwiperSlide>
                            <div className="hero-slide__item">
                                <div className="hero-slide__item__content container">
                                    <div className="hero-slide__item__content__info">
                                        <h2 className="title">Loading...</h2>
                                    </div>
                                </div>
                            </div>
                        </SwiperSlide>
                    )
                }
            </Swiper>
        </div>
    );
}

const isGDriveUrl = (url) => url && (
    url.includes('drive.google.com') || url.includes('drive.usercontent.google.com')
);
const isDropboxUrl = (url) => url && url.includes('dropbox.com');
const resolveDropboxUrl = (url) => {
    if (!url) return url;
    let out = url.replace('www.dropbox.com', 'dl.dropboxusercontent.com');
    out = out.replace(/[?&]dl=0/, m => m.replace('dl=0', 'dl=1'));
    if (!out.includes('dl=1')) out += (out.includes('?') ? '&' : '?') + 'dl=1';
    return out;
};
const extractGDriveFileId = (url) => {
    const m = url.match(/\/file\/d\/([a-zA-Z0-9_-]{25,})/) || url.match(/[?&]id=([a-zA-Z0-9_-]{25,})/);
    return m ? m[1] : null;
};

const HeroSlideItem = ({ item, className, isActive }) => {
    const navigate = useNavigate();
    const [showVideo, setShowVideo] = useState(false);
    const [muted, setMuted] = useState(true);
    const [paused, setPaused] = useState(false);
    const [liked, setLiked] = useState(false);
    const [likeLoading, setLikeLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [resolvedTrailerUrl, setResolvedTrailerUrl] = useState(null);
    const [trailerIsGDrive, setTrailerIsGDrive] = useState(false);
    const videoRef = useRef(null);
    const timerRef = useRef(null);

    const userData = (() => { try { return JSON.parse(localStorage.getItem('USER_LOGIN')); } catch { return null; } })();

    useEffect(() => {
        if (!userData?.id || !item?.id) return;
        fetch(`/movies-favorites-api.php?userId=${userData.id}&movieId=${item.id}`)
            .then(r => r.json())
            .then(d => { if (d.success) setLiked(d.isFavorited); })
            .catch(() => {});
    }, [item?.id]);

    useEffect(() => {
        const url = item.trailerUrl;
        if (!url) { setResolvedTrailerUrl(null); setTrailerIsGDrive(false); return; }
        if (isDropboxUrl(url)) {
            setResolvedTrailerUrl(resolveDropboxUrl(url));
            setTrailerIsGDrive(false);
            return;
        }
        if (isGDriveUrl(url)) {
            const fileId = extractGDriveFileId(url);
            if (fileId) {
                setResolvedTrailerUrl(`https://drive.google.com/file/d/${fileId}/preview`);
                setTrailerIsGDrive(true);
            } else {
                setResolvedTrailerUrl(url);
                setTrailerIsGDrive(false);
            }
            return;
        }
        setResolvedTrailerUrl(url);
        setTrailerIsGDrive(false);
    }, [item.trailerUrl]);

    useEffect(() => {
        if (isActive && item.trailerUrl) {
            timerRef.current = setTimeout(() => setShowVideo(true), 2000);
        } else {
            clearTimeout(timerRef.current);
            setShowVideo(false);
            setPaused(false);
        }
        return () => clearTimeout(timerRef.current);
    }, [isActive, item.trailerUrl]);

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        if (showVideo) {
            v.muted = muted;
            v.play().catch(() => {});
        } else {
            v.pause();
            v.currentTime = 0;
        }
    }, [showVideo]);

    useEffect(() => {
        if (videoRef.current) videoRef.current.muted = muted;
    }, [muted]);

    useEffect(() => {
        const v = videoRef.current;
        if (!v || !showVideo) return;
        if (paused) v.pause();
        else v.play().catch(() => {});
    }, [paused]);

    const tagStyle = {
        backgroundColor: '#444',
        padding: '4px 10px',
        borderRadius: '6px',
        fontSize: '14px',
    };

    const genreStyle = {
        backgroundColor: '#2c2c2c',
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '14px',
    };

    const background = item.posterUrl;
    const title = item.title;

    const formatDuration = (minutes) => {
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        if (h > 0 && m > 0) return `${h}h ${m} phút`;
        if (h > 0 && m === 0) return `${h}h`;
        return `${m} phút`;
    };

    return (
        <div
            className={`hero-slide__item ${className}`}
            style={{
                backgroundImage: `url(${background})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                color: '#fff',
                padding: '100px 40px 120px',
            }}
        >
            {trailerIsGDrive && showVideo && resolvedTrailerUrl && (
                <iframe
                    src={resolvedTrailerUrl}
                    className="hero-video visible"
                    frameBorder="0"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    title="trailer"
                    style={{ border: 0, pointerEvents: 'none' }}
                />
            )}
            {!trailerIsGDrive && resolvedTrailerUrl && (
                <video
                    ref={videoRef}
                    src={resolvedTrailerUrl}
                    className={`hero-video${showVideo ? ' visible' : ''}`}
                    muted
                    loop
                    playsInline
                />
            )}

            <div className="hero-slide__inner" style={{ maxWidth: '800px' }}>
                <h1 style={{
                    fontFamily: '"Segoe UI", Arial, sans-serif',
                    fontSize: 'clamp(28px, 3.8vw, 52px)',
                    fontWeight: 900,
                    marginBottom: '18px',
                    lineHeight: 1.2,
                    letterSpacing: '-0.5px',
                    paddingBottom: '6px',
                    background: 'linear-gradient(160deg, #ffffff 0%, #ffe9b0 55%, #ffc94d 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    filter: 'drop-shadow(0 2px 12px rgba(0,0,0,0.7))',
                }}>
                    {title}
                </h1>

                <div className="hero-tags-row" style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px'}}>
                    <span style={{
                        backgroundColor: '#f5c518',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        color: '#fff',
                        fontWeight: 'bold',
                        fontSize: '14px'
                    }}>
                        MF {item.averageRating}
                    </span>
                    <span style={tagStyle}>T{item.episodeCount}</span>
                    <span style={tagStyle}>{item.releaseYear}</span>
                    <span style={tagStyle}>
                        {item.duration ? formatDuration(item.duration) : "Đang cập nhật"}
                    </span>
                </div>

                <div className="hero-genres-row" style={{display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px'}}>
                    {item.genres.map((genre) => (
                        <span key={genre.id} style={genreStyle}>
                            {genre.name}
                        </span>
                    ))}
                </div>

                <p
                    style={{
                        fontSize: '15px',
                        lineHeight: '1.6',
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}
                >
                    {/* Strip HTML tags để tránh XSS, chỉ hiển thị plain text */}
                    {item.description ? item.description.replace(/<[^>]*>/g, '') : ''}
                </p>
                <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Button
                        onClick={() => {
                            window.location.href = `/watch/${item.slug}/${encryptId(1)}`;
                        }}
                    >
                        <CaretRightOutlined style={{ marginRight: 6 }} />Xem ngay
                    </Button>

                    <button
                        onClick={async (e) => {
                            e.stopPropagation();
                            if (!userData?.id) {
                                alert('Vui lòng đăng nhập để thêm vào yêu thích!');
                                return;
                            }
                            if (likeLoading) return;
                            setLikeLoading(true);
                            try {
                                const res = await fetch('/movies-favorites-api.php', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ userId: userData.id, movieId: item.id }),
                                });
                                const data = await res.json();
                                if (data.success) setLiked(data.isFavorited);
                            } catch {}
                            setLikeLoading(false);
                        }}
                        title={liked ? 'Bỏ thích' : 'Yêu thích'}
                        style={{
                            width: 42, height: 42, borderRadius: '50%',
                            border: `1.5px solid ${liked ? '#ff5050' : 'rgba(255,255,255,0.4)'}`,
                            background: liked ? 'rgba(255,80,80,0.22)' : 'rgba(255,255,255,0.1)',
                            backdropFilter: 'blur(6px)',
                            color: liked ? '#ff5050' : '#fff',
                            fontSize: 16, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.2s',
                            flexShrink: 0,
                            transform: liked ? 'scale(1.12)' : 'scale(1)',
                        }}
                    >
                        <FaHeart />
                    </button>

                    <div style={{ position: 'relative', flexShrink: 0 }}>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                const url = window.location.origin + `/watch/${item.slug}/${encryptId(1)}`;
                                if (navigator.clipboard) {
                                    navigator.clipboard.writeText(url).then(() => {
                                        setCopied(true);
                                        setTimeout(() => setCopied(false), 2000);
                                    });
                                } else {
                                    setCopied(true);
                                    setTimeout(() => setCopied(false), 2000);
                                }
                            }}
                            title="Chia sẻ"
                            style={{
                                width: 42, height: 42, borderRadius: '50%',
                                border: '1.5px solid rgba(255,255,255,0.4)',
                                background: 'rgba(255,255,255,0.1)',
                                backdropFilter: 'blur(6px)',
                                color: '#fff',
                                fontSize: 15, cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all 0.2s',
                            }}
                        >
                            <FaShareAlt />
                        </button>
                        {copied && (
                            <div style={{
                                position: 'absolute', bottom: '110%', left: '50%',
                                transform: 'translateX(-50%)',
                                background: 'rgba(0,0,0,0.8)', color: '#fff',
                                fontSize: 12, padding: '4px 10px', borderRadius: 6,
                                whiteSpace: 'nowrap', pointerEvents: 'none',
                            }}>
                                Đã copy link!
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="hero-slide__item__content__poster">
                <img src={item.posterUrl} alt={item.title} style={{borderRadius: '12px', width: '250px'}}/>
            </div>

            {showVideo && !trailerIsGDrive && (
                <div className="hero-video-controls">
                    <button onClick={() => setPaused(p => !p)} title={paused ? 'Phát' : 'Tạm dừng'}>
                        {paused ? <CaretRightOutlined /> : <PauseOutlined />}
                    </button>
                    <button onClick={() => setMuted(m => !m)} title={muted ? 'Bật âm thanh' : 'Tắt tiếng'}>
                        {muted ? <AudioMutedOutlined /> : <SoundOutlined />}
                    </button>
                </div>
            )}
        </div>
    );
}

export default HeroSlide;
